<template>
  <div v-if="headerName === 'Name'" class="flex items-center gap-8">
    <BittsCheckbox
      :disabled="isCurrentUser"
      :checked="isChecked"
      :aria-label="`Select ${userName}`"
      @click="handleCheckboxChange"
    />
    <BittsAvatar :user="params.data?.user" :show-initials="true" size="small" />
    <b class="text-neutral-text-strong">{{ userName }}</b>
    <BittsTag v-if="isCurrentUser" text="You" color="info" size="x-small" />
  </div>
  <div v-else>
    <BittsTag
      :text="isChecked ? 'Upgrade seat' : 'Remove'"
      :color="isChecked ? 'info' : 'danger'"
      size="x-small"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsCheckbox, BittsTag } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed } from 'vue';

import { AuthOrInvite } from '@/composables/team/useAuthorizationsWithRoles';
import useAuth from '@/composables/useAuth';

export type UserSelectionTableCellParams = AuthOrInvite & {
  roleName: string;
};
export type UserSelectionTableCellContext = {
  onUserSelectionChanged: (
    rowData: UserSelectionTableCellParams,
    isChecked: boolean,
  ) => void;
  isUserSelected: (userId: number | undefined) => boolean;
};
const { params } = defineProps<{
  params: ICellRendererParams<
    UserSelectionTableCellParams,
    UserSelectionTableCellContext
  >;
}>();

const headerName = computed(() => params.colDef?.headerName);

const { currentUser } = useAuth();
const isCurrentUser = computed(
  () => params.data?.user?.id === currentUser.value?.id,
);
const userName = computed(() => {
  if (!params.data?.user?.first_name || !params.data?.user?.last_name) {
    return 'User';
  }
  return `${params.data?.user?.first_name} ${params.data?.user?.last_name}`;
});

const isChecked = computed(
  () => !!params.context?.isUserSelected(params.data?.user?.id),
);

function handleCheckboxChange() {
  if (isCurrentUser.value) return;
  if (params.context?.onUserSelectionChanged && params.data) {
    params.context.onUserSelectionChanged(params.data, !isChecked.value);
  }
}
</script>
